import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Close, Download, Fullscreen, FullscreenExit } from "@mui/icons-material";
import axios from "axios";
import { processesUploadsApi, uploadsApi } from "api";
import uploadBytesApi from "api/uploadBytesApi";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import ReactMarkdown from "react-markdown";
import { safeHtml, safeUrl } from "utils/safeHtml";
import remarkGfm from "remark-gfm";
import mammoth from "mammoth";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import EmlPreview from "./preview/EmlPreview";
import ZipPreview from "./preview/ZipPreview";

interface FilePreviewDialogProps {
  attachmentId: number | null;
  filename: string;
  onClose: () => void;
  /**
   * Optional: when provided, shows a local file preview without any API call.
   * Può essere una funzione: in quel caso i byte si preparano in modo lazy e il
   * dialog si apre subito con la rotella, senza attendere l'estrazione (voci
   * dentro un .zip) prima di mostrare qualcosa.
   */
  localFile?: File | (() => Promise<File>);
  /** Optional: when true, uses uploadsApi instead of processesUploadsApi for fetching the file */
  useUploadsApi?: boolean;
  mainAttachmentId?: number;
  /**
   * URL pubblico da cui leggere i byte (pagina del task condiviso, senza
   * sessione): si scarica con axios grezzo e si serve come data URI, così le
   * anteprime funzionano anche da sloggati. Se assente, percorso autenticato.
   */
  fetchUrl?: string;
}

/**
 * Sorgente di un'anteprima annidata (voce di archivio o allegato di una mail).
 * `load` è lazy: il dialog del chiamante si apre subito con la rotella e i byte
 * arrivano quando la promessa si risolve.
 */
export interface PreviewTarget {
  name: string;
  load: () => Promise<File>;
}

// Stesse immagini di `IMAGE_EXTENSIONS` (common/utilities): se il formato è
// accettato in upload deve anche avere l'anteprima, dentro lo zip come fuori.
const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "ico", "apng", "avif"];
const PDF_EXTS = ["pdf"];
const VIDEO_EXTS = ["mp4", "webm", "mov", "avi", "mkv"];
const AUDIO_EXTS = ["mp3", "wav", "ogg", "m4a", "flac"];
const TEXT_EXTS = ["txt", "csv", "json", "xml", "yaml", "yml", "log", "ts", "tsx", "js", "jsx", "html", "css", "java"];
const MD_EXTS = ["md"];
const DOCX_EXTS = ["docx"];
const XLSX_EXTS = ["xlsx", "xls", "ods"];
const PPTX_EXTS = ["pptx"];
const PPT_EXTS = ["ppt"];
const ALL_PPT_EXTS = ["pptx", "ppt"];
const OFFICE_EXTS = ["docx", "xlsx", "xls", "odt", "ods"];
const EML_EXTS = ["eml"];
const ZIP_EXTS = ["zip"];

function getExtension(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() ?? "";
}

function getMimeType(ext: string): string {
  if (IMAGE_EXTS.includes(ext)) {
    if (ext === "svg") return "image/svg+xml";
    if (ext === "gif") return "image/gif";
    if (ext === "webp") return "image/webp";
    if (ext === "bmp") return "image/bmp";
    if (ext === "ico") return "image/x-icon";
    if (ext === "apng") return "image/apng";
    if (ext === "avif") return "image/avif";
    return `image/${ext === "jpg" ? "jpeg" : ext}`;
  }
  if (ext === "pdf") return "application/pdf";
  if (ext === "mp4") return "video/mp4";
  if (ext === "webm") return "video/webm";
  if (ext === "mov") return "video/quicktime";
  if (ext === "mp3") return "audio/mpeg";
  if (ext === "wav") return "audio/wav";
  if (ext === "ogg") return "audio/ogg";
  if (ext === "m4a") return "audio/mp4";
  if (ext === "docx") return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (ext === "xlsx" || ext === "xls") return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (ext === "pptx") return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
  if (ext === "ppt") return "application/vnd.ms-powerpoint";
  if (ext === "odt") return "application/vnd.oasis.opendocument.text";
  if (ext === "ods") return "application/vnd.oasis.opendocument.spreadsheet";
  if (ext === "eml") return "message/rfc822";
  if (ext === "zip") return "application/zip";
  return "application/octet-stream";
}

/** Convert a base64 string (data URI o base64 puro) to an ArrayBuffer.
 *  Tollerante: toglie whitespace, normalizza l'alfabeto URL-safe e ripadda.
 *  Non lancia mai: in caso di input irrecuperabile ritorna un buffer vuoto,
 *  così il chiamante può comunque provare altre strade (es. il data URI). */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const clean = (base64.includes(",") ? base64.split(",")[1] : base64).replace(/\s+/g, "");
  const normalized = clean.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  try {
    const binaryStr = atob(padded);
    const len = binaryStr.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    return bytes.buffer;
  } catch {
    return new ArrayBuffer(0);
  }
}

const FilePreviewDialog: React.FC<FilePreviewDialogProps> = ({
  attachmentId,
  filename,
  onClose,
  localFile,
  useUploadsApi = false,
  mainAttachmentId,
  fetchUrl,
}) => {
  const translations = useTranslations();
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [xlsxSheets, setXlsxSheets] = useState<{ name: string; data: any[][] }[] | null>(null);
  const [officeError, setOfficeError] = useState(false);
  const [fileBlob, setFileBlob] = useState<Blob | null>(null);
  const [error, setError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pptxPreview, setPptxPreview] = useState<{ thumbnailUrl: string | null; slideCount: number } | null>(null);
  const [archiveBuffer, setArchiveBuffer] = useState<ArrayBuffer | null>(null);
  const [nestedFile, setNestedFile] = useState<{ name: string; load: () => Promise<File> } | null>(null);

  const ext = getExtension(filename);
  const open = attachmentId !== null || localFile !== undefined;

  /** Extract PPTX thumbnail using jszip — PPTX is a ZIP with a docProps/thumbnail image */
  const extractPptxPreview = useCallback(async (arrayBuffer: ArrayBuffer) => {
    try {
      const zip = await JSZip.loadAsync(arrayBuffer);
      // Count slides
      const slideCount = Object.keys(zip.files).filter((name) =>
        /^ppt\/slides\/slide\d+\.xml$/.test(name)
      ).length;
      // Thumbnail candidates (Office 2010+ embeds one here)
      const thumbnailCandidates = [
        "docProps/thumbnail.jpeg",
        "docProps/thumbnail.jpg",
        "docProps/thumbnail.png",
        "docProps/thumbnail.emf",
      ];
      let thumbnailUrl: string | null = null;
      for (const candidate of thumbnailCandidates) {
        const entry = zip.file(candidate);
        if (entry) {
          const blob = await entry.async("blob");
          const mime = candidate.endsWith(".png") ? "image/png" : "image/jpeg";
          thumbnailUrl = URL.createObjectURL(new Blob([blob], { type: mime }));
          break;
        }
      }
      setPptxPreview({ thumbnailUrl, slideCount });
    } catch {
      // JSZip failed (corrupt/non-standard ZIP) — show clean placeholder, not an error
      setPptxPreview({ thumbnailUrl: null, slideCount: 0 });
    }
  }, []);

  const readLocalFile = useCallback(async (source: File | (() => Promise<File>)) => {
    setLoading(true);
    setError(false);
    setOfficeError(false);
    setDocxHtml(null);
    setXlsxSheets(null);
    setMarkdownContent(null);
    setBlobUrl(null);
    setTextContent(null);
    setFileBlob(null);
    setPptxPreview(null);
    setArchiveBuffer(null);

    try {
      // Sorgente lazy (voce di archivio): l'attesa va coperta dalla rotella,
      // quindi `loading` è già true mentre questa promessa si risolve.
      const file = typeof source === "function" ? await source() : source;
      setFileBlob(file);

      if (EML_EXTS.includes(ext) || ZIP_EXTS.includes(ext)) {
        setArchiveBuffer(await file.arrayBuffer());
        return;
      }

      if (MD_EXTS.includes(ext)) {
        const text = await file.text();
        setMarkdownContent(text);
        return;
      }

      if (DOCX_EXTS.includes(ext)) {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setDocxHtml(result.value);
        } catch {
          setOfficeError(true);
        }
        return;
      }

      if (XLSX_EXTS.includes(ext)) {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          const sheets = workbook.SheetNames.map((name: any) => {
            const sheet = workbook.Sheets[name];
            const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
            return { name, data };
          });
          setXlsxSheets(sheets);
        } catch {
          setOfficeError(true);
        }
        return;
      }

      if (PPTX_EXTS.includes(ext)) {
        const arrayBuffer = await file.arrayBuffer();
        await extractPptxPreview(arrayBuffer);
        return;
      }

      if (PPT_EXTS.includes(ext)) {
        // Old binary format — cannot extract with JSZip, show placeholder directly
        setPptxPreview({ thumbnailUrl: null, slideCount: 0 });
        return;
      }

      if (TEXT_EXTS.includes(ext)) {
        const text = await file.text();
        setTextContent(text);
        return;
      }

      if (
        IMAGE_EXTS.includes(ext) ||
        PDF_EXTS.includes(ext) ||
        VIDEO_EXTS.includes(ext) ||
        AUDIO_EXTS.includes(ext) ||
        OFFICE_EXTS.includes(ext)
      ) {
        // Stesso MIME del percorso server: una voce estratta da uno zip arriva
        // come `application/octet-stream` e con quel tipo `<embed>` (PDF) e i
        // media non si rendono.
        const objectUrl = URL.createObjectURL(new Blob([file], { type: getMimeType(ext) }));
        setBlobUrl(objectUrl);
      }
    } finally {
      setLoading(false);
    }
  }, [ext, extractPptxPreview]);

  const fetchServerFile = useCallback(async (attachmentId: number, parentId?: number) => {
    setLoading(true);
    setError(false);
    setOfficeError(false);
    setDocxHtml(null);
    setXlsxSheets(null);
    setMarkdownContent(null);
    setBlobUrl(null);
    setTextContent(null);
    setFileBlob(null);
    setPptxPreview(null);
    setArchiveBuffer(null);

    const mime = getMimeType(ext);
    try {
      // Se abbiamo un URL pubblico e il tipo si rende **direttamente**
      // (immagine/pdf/video/audio) lo passiamo così com'è: è esattamente quello
      // che fa la miniatura nella lista, che funziona sempre. Nessuna XHR,
      // quindi nessun CORS e nessuna risposta da interpretare.
      if (
        fetchUrl &&
        (IMAGE_EXTS.includes(ext) ||
          PDF_EXTS.includes(ext) ||
          VIDEO_EXTS.includes(ext) ||
          AUDIO_EXTS.includes(ext))
      ) {
        setBlobUrl(fetchUrl);
        return;
      }
      // Si ragiona sempre su byte (ArrayBuffer): il percorso autenticato riceve
      // un data URI base64, quello pubblico i byte grezzi. Nessun andata/ritorno
      // base64, che sui file grandi è lento e fragile.
      let bytes: ArrayBuffer;
      // Se la sorgente è già un data URI (endpoint autenticato) lo teniamo:
      // è la stessa forma che la lista usa con successo per la miniatura,
      // quindi si rende sicuramente in `<img>`.
      let sourceUri: string | null = null;
      if (fetchUrl) {
        // Percorso pubblico: axios grezzo (nessun JWT, nessun interceptor — un
        // 401/404 qui non deve far scattare il logout globale).
        const res = await axios.get<ArrayBuffer>(fetchUrl, {
          responseType: "arraybuffer",
        });
        bytes = res.data;
      } else if (!useUploadsApi) {
        // Stesso percorso che funziona da sloggati: i byte dal backend
        // statistiche, grezzi. Se non sono disponibili si ricade sul Delphi.
        try {
          const res = await uploadBytesApi.get<ArrayBuffer>(`/${attachmentId}`);
          bytes = res.data;
        } catch {
          const { data } = await processesUploadsApi.get(`/${attachmentId}`);
          if (typeof data.blob === "string" && data.blob.startsWith("data:")) {
            sourceUri = data.blob;
          }
          bytes = base64ToArrayBuffer(data.blob);
        }
      } else {
        const endpoint = parentId
          ? `/${parentId}/histories/${attachmentId}`
          : `/${attachmentId}`;
        const { data } = await uploadsApi.get(endpoint);
        if (typeof data.blob === "string" && data.blob.startsWith("data:")) {
          sourceUri = data.blob;
        }
        bytes = base64ToArrayBuffer(data.blob);
      }
      const blob = new Blob([bytes], { type: mime });
      setFileBlob(blob);

      // Anteprima archivi/email: la voce non si rende da un blob URL, serve il
      // buffer. `bytes` è già la sorgente unica (percorso pubblico `fetchUrl` e
      // percorso autenticato convergono qui), quindi vale anche nella pagina
      // del task condiviso, dove non c'è sessione.
      if (EML_EXTS.includes(ext) || ZIP_EXTS.includes(ext)) {
        setArchiveBuffer(bytes);
        return;
      }

      if (MD_EXTS.includes(ext)) {
        setMarkdownContent(new TextDecoder().decode(bytes));
        return;
      }

      if (DOCX_EXTS.includes(ext)) {
        try {
          const result = await mammoth.convertToHtml({ arrayBuffer: bytes });
          setDocxHtml(result.value);
        } catch {
          setOfficeError(true);
        }
        return;
      }

      if (XLSX_EXTS.includes(ext)) {
        try {
          const workbook = XLSX.read(bytes, { type: "array" });
          const sheets = workbook.SheetNames.map((name: any) => {
            const sheet = workbook.Sheets[name];
            const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
            return { name, data };
          });
          setXlsxSheets(sheets);
        } catch {
          setOfficeError(true);
        }
        return;
      }

      if (PPTX_EXTS.includes(ext)) {
        await extractPptxPreview(bytes);
        return;
      }

      if (PPT_EXTS.includes(ext)) {
        // Old binary format — cannot extract with JSZip, show placeholder directly
        setPptxPreview({ thumbnailUrl: null, slideCount: 0 });
        return;
      }

      if (TEXT_EXTS.includes(ext)) {
        setTextContent(new TextDecoder().decode(bytes));
        return;
      }

      if (
        IMAGE_EXTS.includes(ext) ||
        PDF_EXTS.includes(ext) ||
        VIDEO_EXTS.includes(ext) ||
        AUDIO_EXTS.includes(ext) ||
        OFFICE_EXTS.includes(ext)
      ) {
        setBlobUrl(sourceUri ?? URL.createObjectURL(blob));
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [ext, extractPptxPreview, useUploadsApi, fetchUrl]);

  useEffect(() => {
    if (!open) return;
    if (localFile) {
      readLocalFile(localFile).catch(() => setError(true));
    } else if (attachmentId !== null) {
      fetchServerFile(attachmentId, mainAttachmentId);
    }
  }, [open, localFile, attachmentId, mainAttachmentId, readLocalFile, fetchServerFile]);

  // Cleanup blobUrl and pptx thumbnail on close
  useEffect(() => {
    if (!open) {
      if (blobUrl) {
        // Può essere un data URI (percorso autenticato) o un blob URL.
        if (blobUrl.startsWith("blob:")) URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
      if (pptxPreview?.thumbnailUrl) {
        URL.revokeObjectURL(pptxPreview.thumbnailUrl);
        setPptxPreview(null);
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = async () => {
    // Il blob già creato per il rendering, oppure uno al volo dai byte, oppure —
    // se stiamo rendendo direttamente dall'URL pubblico — si scaricano i byte
    // adesso (così il download non apre il file in una pagina).
    let href: string | null = blobUrl || (fileBlob ? URL.createObjectURL(fileBlob) : null);
    let revoke = href !== null && !blobUrl;
    if (!href && fetchUrl) {
      try {
        const res = await axios.get(fetchUrl, { responseType: "blob" });
        href = URL.createObjectURL(res.data);
        revoke = true;
      } catch {
        return;
      }
    }
    if (!href) return;
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    if (revoke) URL.revokeObjectURL(href);
  };

  const renderXlsxSheet = (sheet: { name: string; data: any[][] }) => (
    <Box key={sheet.name} sx={{ mb: 3 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: "primary.main" }}>
        {sheet.name}
      </Typography>
      <Box sx={{ overflowX: "auto", maxHeight: isFullscreen ? "calc(100vh - 140px)" : "60vh", overflow: "auto" }}>
        <Table size="small" sx={{ minWidth: "max-content" }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "action.hover" }}>
              {sheet.data[0]?.map((cell: any, i: number) => (
                <TableCell key={i} sx={{ fontWeight: 700, whiteSpace: "nowrap", border: "1px solid", borderColor: "divider" }}>
                  {String(cell ?? "")}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sheet.data.slice(1).map((row: any[], ri: number) => (
              <TableRow key={ri}>
                {row.map((cell: any, ci: number) => (
                  <TableCell key={ci} sx={{ whiteSpace: "nowrap", border: "1px solid", borderColor: "divider", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {String(cell ?? "")}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );

  const renderPreview = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} minHeight={300}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} minHeight={300}>
          <Typography color="error">
            {getTranslation(translations, "generic.error") || "Errore nel caricamento del file."}
          </Typography>
        </Box>
      );
    }

    if (officeError) {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flex={1} gap={2} minHeight={200}>
          <Typography color="text.secondary">
            {getTranslation(translations, "attachment.preview.label.not.available") || "Anteprima non disponibile per questo tipo di file."}
          </Typography>
        </Box>
      );
    }

    if (IMAGE_EXTS.includes(ext) && blobUrl) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} sx={{ overflow: "auto", p: 1 }}>
          <img
            src={blobUrl}
            alt={filename}
            style={{ maxWidth: "100%", maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh", objectFit: "contain" }}
          />
        </Box>
      );
    }

    if (PDF_EXTS.includes(ext) && blobUrl) {
      return (
        <Box flex={1} sx={{ minHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh", display: "flex", flexDirection: "column" }}>
          <embed
            src={blobUrl}
            type="application/pdf"
            width="100%"
            height="100%"
            style={{ flex: 1, minHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh" }}
          />
        </Box>
      );
    }

    if (VIDEO_EXTS.includes(ext) && blobUrl) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <video controls style={{ maxWidth: "100%", maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh" }}>
            <source src={blobUrl} type={getMimeType(ext)} />
          </video>
        </Box>
      );
    }

    if (AUDIO_EXTS.includes(ext) && blobUrl) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} p={3}>
          <audio controls style={{ width: "100%" }}>
            <source src={blobUrl} type={getMimeType(ext)} />
          </audio>
        </Box>
      );
    }

    if (MD_EXTS.includes(ext) && markdownContent !== null) {
      return (
        <Box
          flex={1}
          sx={{
            overflow: "auto",
            p: 3,
            bgcolor: "background.default",
            color: "text.primary",
            borderRadius: 1,
            maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh",
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ href, children, ...rest }) => {
                const safeHref = safeUrl(href);
                if (!safeHref) return <span>{children}</span>;
                return (
                  <a {...rest} href={safeHref} target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                );
              },
              code({ node, inline, className, children, ...props }: any) {
                const match = /language-(\w+)/.exec(className || "");
                return !inline && match ? (
                  <SyntaxHighlighter
                    {...props}
                    style={vscDarkPlus as any}
                    language={match[1]}
                    PreTag="div"
                  >
                    {String(children).replace(/\n$/, "")}
                  </SyntaxHighlighter>
                ) : (
                  <code {...props} className={className}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {markdownContent}
          </ReactMarkdown>
        </Box>
      );
    }

    if (DOCX_EXTS.includes(ext) && docxHtml !== null) {
      return (
        <Box
          flex={1}
          sx={{
            overflow: "auto",
            p: 3,
            bgcolor: "background.paper",
            color: "text.primary",
            borderRadius: 1,
            maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh",
            "& table": { borderCollapse: "collapse", width: "100%" },
            "& td, & th": { border: "1px solid", borderColor: "divider", padding: "6px 10px" },
            "& th": { backgroundColor: "action.hover", fontWeight: 700 },
          }}
          dangerouslySetInnerHTML={{ __html: safeHtml(docxHtml) }}
        />
      );
    }

    if ((XLSX_EXTS.includes(ext)) && xlsxSheets !== null) {
      return (
        <Box
          flex={1}
          sx={{
            overflow: "auto",
            p: 2,
            bgcolor: "background.paper",
            color: "text.primary",
            borderRadius: 1,
            maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh",
          }}
        >
          {xlsxSheets.map((sheet) => renderXlsxSheet(sheet))}
        </Box>
      );
    }

    if (ALL_PPT_EXTS.includes(ext) && pptxPreview !== null) {
      const { thumbnailUrl, slideCount } = pptxPreview;
      return (
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
          sx={{
            overflow: "auto",
            bgcolor: "background.default",
            color: "text.primary",
            borderRadius: 1,
            minHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh",
            p: 3,
          }}
        >
          {thumbnailUrl ? (
            <>
              <Box
                component="img"
                src={thumbnailUrl}
                alt={filename}
                sx={{
                  maxWidth: "100%",
                  maxHeight: isFullscreen ? "calc(100vh - 160px)" : "55vh",
                  objectFit: "contain",
                  borderRadius: 2,
                  boxShadow: "0 4px 24px rgba(0,0,0,0.18)",
                  bgcolor: "#fff",
                }}
              />
              {slideCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {slideCount} {slideCount === 1 ? "slide" : "slides"}
                  {" — "}
                  {getTranslation(translations, "attachment.preview.first.slide") || "Anteprima prima diapositiva"}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Box
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "action.hover",
                  fontSize: 48,
                }}
              >
                📊
              </Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {filename}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {slideCount > 0
                  ? `${slideCount} ${slideCount === 1 ? "slide" : "slides"}`
                  : ""}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getTranslation(translations, "attachment.preview.label.not.available") || "Anteprima miniatura non disponibile — scarica il file per visualizzarlo."}
              </Typography>
              <FlowentiButton
                variant="outlined"
                size="small"
                onClick={() => handleDownload()}
              >
                {getTranslation(translations, "settings.download.label") || "Scarica"}
              </FlowentiButton>
            </>
          )}
        </Box>
      );
    }

    if (ALL_PPT_EXTS.includes(ext) && pptxPreview === null && !loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1} minHeight={300}>
          <CircularProgress />
        </Box>
      );
    }

    if (TEXT_EXTS.includes(ext) && textContent !== null) {
      const languageMap: Record<string, string> = {
        ts: "typescript",
        tsx: "tsx",
        js: "javascript",
        jsx: "jsx",
        json: "json",
        html: "html",
        css: "css",
        xml: "xml",
        yaml: "yaml",
        yml: "yaml",
        csv: "csv",
        log: "log",
        txt: "plaintext",
        java: "java"
      };
      const language = languageMap[ext] || "plaintext";

      return (
        <Box
          flex={1}
          sx={{
            overflow: "auto",
            p: 2,
            bgcolor: "#1E1E1E",
            borderRadius: 1,
            maxHeight: isFullscreen ? "calc(100vh - 64px)" : "70vh",
          }}
        >
          <SyntaxHighlighter
            style={vscDarkPlus as any}
            language={language}
            PreTag="div"
            customStyle={{ margin: 0, padding: 0, backgroundColor: "transparent" }}
            showLineNumbers={true}
          >
            {textContent}
          </SyntaxHighlighter>
        </Box>
      );
    }

    if (EML_EXTS.includes(ext) && archiveBuffer) {
      return <EmlPreview buffer={archiveBuffer} onPreviewFile={setNestedFile} />;
    }

    if (ZIP_EXTS.includes(ext) && archiveBuffer) {
      return <ZipPreview buffer={archiveBuffer} onPreviewFile={setNestedFile} />;
    }

    // Fallback
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        flex={1}
        gap={2}
        minHeight={200}
      >
        <Typography color="text.secondary">
          {getTranslation(translations, "attachment.preview.label.not.available") || "Anteprima non disponibile per questo tipo di file."}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isFullscreen}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: isFullscreen ? 0 : 2,
          minHeight: isFullscreen ? "100vh" : "60vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} flex={1} noWrap title={filename}>
          {filename}
        </Typography>

        {(blobUrl || fileBlob) && (
          <Tooltip title={getTranslation(translations, "settings.download.label") || "Download"}>
            <IconButton size="small" onClick={handleDownload}>
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={isFullscreen
          ? (getTranslation(translations, "generic.exitfullscreen") || "Esci dalla schermata intera")
          : (getTranslation(translations, "generic.fullscreen") || "Schermata intera")
        }>
          <IconButton size="small" onClick={() => setIsFullscreen((prev) => !prev)}>
            {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Tooltip title={getTranslation(translations, "pm.minimap.close.lable") || "Chiudi"}>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <DialogContent
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "auto",
        }}
      >
        {renderPreview()}
      </DialogContent>
      </Dialog>

      {nestedFile && (
        <FilePreviewDialog
          filename={nestedFile.name}
          attachmentId={null}
          localFile={nestedFile.load}
          onClose={() => setNestedFile(null)}
        />
      )}
    </>
  );
};

export default FilePreviewDialog;
